/**
 * Runtime for the build-time bound copy of the one-off pages.
 *
 * PAWTENANT-CONTENT-STUDIO-COVERAGE-001
 *
 * scripts/cms/autobind.mjs rewrites each bound literal of a page module into
 * one of these calls. Every call returns the published override for (route,
 * id) when it is valid, and otherwise the default it was given, so with
 * nothing published the page renders exactly the string it always did.
 *
 *   __cmsT    text (headings, paragraphs, list items, FAQ, alt text, labels).
 *             The default's leading / trailing spaces are kept around an
 *             override, so inline spacing next to links and bold text holds.
 *   __cmsImg  an image path; an override must be an uploaded studio image.
 *   __cmsLink an internal content-link destination; an override must be an
 *             internal path outside the order funnel.
 */

import { cmsOverrides } from "./bundle";
import type { CmsImageOverride } from "./types";

const FUNNEL = /^\/(assessment|psd-assessment|checkout|continue|consultation|account|my-orders|customer-login|provider|partner|admin|verify|r\/|v\/)/;

function override(route: string, id: string): unknown {
  const o = cmsOverrides(route);
  return o ? o[id] : undefined;
}

export function __cmsT(route: string, id: string, def: string): string {
  const v = override(route, id);
  if (typeof v !== "string") return def;
  const text = v.replace(/\s+/g, (ws) => (ws.includes("\n") && def.includes("\n") ? ws : " ")).trim();
  if (!text) return def;
  const m = /^(\s*)[\s\S]*?(\s*)$/.exec(def);
  return `${m?.[1] ?? ""}${text}${m?.[2] ?? ""}`;
}

export function __cmsImg(route: string, id: string, def: string): string {
  const v = override(route, id) as CmsImageOverride | undefined;
  if (v && typeof v === "object" && typeof v.src === "string" && /^(https:\/\/[^\s"'<>]+|\/(?!\/)[^\s"'<>]+)$/.test(v.src)) return v.src;
  return def;
}

export function __cmsLink(route: string, id: string, def: string): string {
  const v = override(route, id);
  if (typeof v !== "string") return def;
  const to = v.trim();
  return /^\/(?!\/)[a-z0-9\-/#]*$/.test(to) && !FUNNEL.test(to) ? to : def;
}
